import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Sparkles, Loader2, AlertTriangle, CheckCircle2, Info, TrendingUp, Wallet, Truck, ScrollText } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICONE_AGENTE = { financeiro: Wallet, logistica: Truck, fiscal: ScrollText };
const NOME_AGENTE = { financeiro: "Financeiro", logistica: "Logística", fiscal: "Fiscal" };
const COR_NIVEL = { ok: "text-emerald-400", info: "text-blue-400", aviso: "text-yellow-400", critico: "text-red-400" };
const ICONE_NIVEL = { ok: CheckCircle2, info: Info, aviso: AlertTriangle, critico: AlertTriangle };

/**
 * Painel de Inteligência centralizado - Copiloto, Agentes de
 * monitoramento e Previsor de Gosto, tudo em um só lugar, servindo
 * tanto afiliado quanto fornecedor.
 */
const PainelInteligencia = ({ usuarioId }) => {
  const ehFornecedor = authService.getUser()?.role === "fornecedor";
  const [copiloto, setCopiloto] = useState(null);
  const [agentes, setAgentes] = useState(null);
  const [previsor, setPrevisor] = useState(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    const carregar = async () => {
      if (!usuarioId) return;
      setCarregando(true);
      try {
        const chamadas = [
          authService.fetchAuth(`${BACKEND_URL}/api/ia-global/copiloto/${usuarioId}`),
          authService.fetchAuth(`${BACKEND_URL}/api/agentes/resumo/${usuarioId}`),
        ];
        if (!ehFornecedor) {
          chamadas.push(authService.fetchAuth(`${BACKEND_URL}/api/ia-global/previsor-gosto/${usuarioId}`));
        }
        const [respCopiloto, respAgentes, respPrevisor] = await Promise.all(chamadas);
        if (respCopiloto.ok) setCopiloto(await respCopiloto.json());
        if (respAgentes.ok) setAgentes(await respAgentes.json());
        if (respPrevisor && respPrevisor.ok) setPrevisor(await respPrevisor.json());
      } catch (error) {
        console.error("Erro ao carregar painel de inteligência:", error);
      } finally {
        setCarregando(false);
      }
    };
    carregar();
  }, [usuarioId]);

  if (carregando) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Carregando...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Sparkles className="w-7 h-7 text-orange-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Painel de Inteligência</h1>
          <p className="text-sm text-gray-400">Copiloto, alertas automáticos e sugestões de produto</p>
        </div>
      </div>

      {copiloto && (
        <div>
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Copiloto</h2>
          <div className="space-y-2">
            {copiloto.sugestoes.map((s, i) => (
              <Card key={i} className="p-3 bg-gray-800/50 border-gray-700">
                <p className="text-white font-medium text-sm">{s.titulo}</p>
                <p className="text-gray-400 text-xs mt-1">{s.mensagem}</p>
              </Card>
            ))}
          </div>
        </div>
      )}

      {agentes && (
        <div>
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Agentes de Monitoramento</h2>
          <div className="space-y-2">
            {agentes.alertas.map((a, i) => {
              const IconeAgente = ICONE_AGENTE[a.agente] || Info;
              const IconeNivel = ICONE_NIVEL[a.nivel] || Info;
              return (
                <Card key={i} className="p-3 bg-gray-800/50 border-gray-700 flex items-start gap-3">
                  <IconeAgente className="w-4 h-4 text-gray-500 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-[10px] text-gray-500 uppercase">{NOME_AGENTE[a.agente]}</p>
                    <p className="text-gray-300 text-sm">{a.mensagem}</p>
                  </div>
                  <IconeNivel className={`w-4 h-4 shrink-0 ${COR_NIVEL[a.nivel]}`} />
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {!ehFornecedor && previsor && previsor.sugestoes.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Produtos que combinam com você
          </h2>
          <div className="grid grid-cols-2 gap-2">
            {previsor.sugestoes.map((p) => (
              <Card key={p.produto_id} className="p-3 bg-gray-800/50 border-gray-700">
                <p className="text-white font-medium text-sm">{p.nome}</p>
                <p className="text-emerald-400 text-xs mt-1">R$ {(p.preco || 0).toFixed(2)}</p>
                <p className="text-gray-500 text-[11px] mt-1">{p.motivo}</p>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PainelInteligencia;
